import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useUser, MedicalDate } from '../context/UserContext';
import { useNavigation } from '../context/NavigationContext';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addMonths, parseISO, format } from 'date-fns';
import { es } from 'date-fns/locale';

import './medicalDateForm.css'
import NotAvailableHour from '../components/NotAvailableHour';
import { medicalImg } from '../data/photos'
import Spinner from '../components/Spinner';
import LogRocket from 'logrocket';
import ProgressBar from '../components/ProgressBar';

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY!;

registerLocale('es', es);


// Esquema de validación con Yup
const FormSchema = Yup.object().shape({
  docId: Yup.string().required('Seleccione un médico'),
  dia: Yup.date().required('Seleccione un día'),
  hora: Yup.string().required('La hora es obligatoria'),
});

interface Specialists {
  subspecialty: string;
  cod: string;
  names: string;
  firstLastname: string;
  secondLastname: string;
}

interface AvailableHour {
  availableHour: string;
  codHour: string;
}

interface BodyData {
  codHour?: string;
  patientCod?: string;
}

const MedicalDateForm: React.FC = () => {
  const { date, setDate, user } = useUser();
  const { handleNavigationFromRoot } = useNavigation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(date.dia);
  const [specialists, setSpecialists] = useState<Specialists[]>([]);
  const [selectedSpecialist, setSelectedSpecialist] = useState('');
  const [isNotAvailableDays, setIsNotAvailableDays] = useState(false);
  const [availableDays, setAvailableDays] = useState<Date[]>([]);
  const [selectedHour, setSelectedHour] = useState<string>('');
  const [turnsByDate, setTurnsByDate] = useState<AvailableHour[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageCode, setMessageCode] = useState("default");
  const [medicalPhoto, setMedicalPhoto] = useState("images/md-del-usuario.png");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCheckHour, setIsLoadingCheckHour] = useState(false);
  const [specialism, setSpecialism] = useState<string>('Seleccionar médico para ver especialidad.');
  const [isIdentified, setIsIdentified] = useState(false);

  let isNotChangueDate = true;

  useEffect(() => {
    if (date.hora !== "") {
      fetchFreeHour();
      setDate({
        ...date,
        hourId: '',
        hora: ''
      });
    }
  }, [date.docId]);

  const fetchFreeHour = async () => {
    const url = `${apiUrl}/api/free-hour`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        },
        body: JSON.stringify({ codHour: date.hourId })
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const data = await response.json();
    } catch (error) {
      console.error('Error al enviar la solicitud:', error);
    }
  };


  useEffect(() => {
    const fetchSpecialists = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/get-specialists`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        })
        const data = await response.json();
        setSpecialists(data.data);
        if (date.docId !== "") {
          getSpecialism(date.docId, data.data)
        }
      } catch (error) {
        console.error('Error al obtener los nombres:', error);
      }
    };
    fetchSpecialists();

    if (user && user.docId && !isIdentified) {
      console.log("Identified OK, with codId: ", user.docId);
      LogRocket.identify(user.docId, {
        docId: user.docId,
        name: `${user.name} ${user.lastName}`,
      });
      setIsIdentified(true);
    }
  }, []);

  useEffect(() => {
    if (date.docId !== "") {
      setSelectedSpecialist(date.docId)
      fetchAvailableDates(date.docId)
      getMedicalPhoto(date.docId)
    }
  }, [date.docId]);

  const reloadTurnsByDate = () => {
    fetchTurnsByDate(selectedSpecialist, selectedDate);
    setIsModalOpen(false)
  };

  useEffect(() => {
    const fetchMedicalPlans = async () => {
      const bodyData = {
        'medicalPlanType': user.healthInsurance
      }
      try {
        const response = await fetch(
          `${apiUrl}/api/get-medical-plans`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          },
          body: JSON.stringify(bodyData)
        })
        const data = await response.json();
        if (data.data.length === 1) {
          user.medicalplan = data.data[0].cod
        } else {
          user.medicalplan = ''
        }
      } catch (error) {
        console.error('Error al obtener los nombres:', error);
      }
    };
    fetchMedicalPlans();
  }, []);

  const fetchAvailableDates = async (medicoId: string) => {
    try {
      const url = new URL(`${apiUrl}/api/get-available-dates`);
      url.searchParams.append('codSpecialist', medicoId);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        }
      })
      const data = await response.json();
      if (data.data.length === 0) {
        setAvailableDays([]);
        setIsNotAvailableDays(true);
      } else {
        const availableDates = data.data.map((day: string) => {
          return parseISO(day)
        }
        )
        let firstDay;
        if (date.isPreRegister && isNotChangueDate) {
          firstDay = date.dia;
        } else {
          firstDay = availableDates[0];
          setSelectedDate(availableDates[0]);
        }
        setAvailableDays(availableDates);
        fetchTurnsByDate(medicoId, firstDay);
        setIsNotAvailableDays(false);
      }
    } catch (error) {
      console.error('Error al obtener los días disponible:', error);
    }
  };

  const fetchTurnsByDate = async (medicoId: string, date: Date) => {
    setIsLoading(true);
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const url = new URL(`${apiUrl}/api/get-hours-by-date`);
      url.searchParams.append('codSpecialist', medicoId);
      url.searchParams.append('date', formattedDate);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        }
      })
      const data = await response.json();
      setIsLoading(false);
      setTurnsByDate(data.data);
    } catch (error) {
      console.error('Error al obtener las horas disponibles:', error);
    }
  };

  const fetchCheckHour = async (valuesDate: MedicalDate) => {
    setIsLoadingCheckHour(true);
    const url = `${apiUrl}/api/check-hour`;
    let bodyData: BodyData = {
      codHour: valuesDate.hourId,
    };

    if (user.patientCod !== "") {
      bodyData = {
        codHour: valuesDate.hourId,
        patientCod: user.patientCod
      };
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        },
        body: JSON.stringify(bodyData)
      });

      const data = await response.json();
      if (!response.ok) {
        setMessageCode(data.data.code);
        setIsLoadingCheckHour(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setDate({
        ...date,
        ...valuesDate,
      });
      setIsLoadingCheckHour(false);
      navigate('/confirmation');
    } catch (error) {
      setIsModalOpen(true);
      console.error('Error al enviar la solicitud:', error);
    }
  };

  const getMedicalPhoto = (docId: string) => {
    const photo = medicalImg[docId];
    photo !== undefined ? (setMedicalPhoto(photo)) : (setMedicalPhoto("images/md-del-usuario.png"))
  }

  const initialValues = {
    medico: date.medico,
    docId: date.docId,
    dia: selectedDate,
    hourId: date.hourId,
    hora: date.hora,
  };

  const getSpecialism = (docId: string, data?: Specialists[]) => {
    const updatedSpacialists = data || specialists;
    const found = updatedSpacialists.find(item => item.cod === docId);

    return found ? setSpecialism(found.subspecialty) : setSpecialism('');
  }


  return (
    <>
      <ProgressBar loadPercentage={50} />
      <div className='date-data-form'>
        {isLoadingCheckHour && <div className='date-spinner-item'>
          <Spinner />
        </div>}
        <Formik
          initialValues={initialValues}
          validationSchema={FormSchema}
          onSubmit={(values: any) => {
            handleNavigationFromRoot();
            const dateValues = {
              dia: selectedDate,
              docId: values.docId,
              hora: values.hora,
              hourId: values.hourId,
              medico: values.medico,
              isPreRegister: true
            }
            fetchCheckHour(dateValues);
          }}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className='medical-data-item date-data-item'>
                <div className='date-select-item'>
                  <div className='select-item'>
                    <label htmlFor="docId">Médico:</label>
                    <Field
                      as="select"
                      name="docId"
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        const selectedIndex = event.target.selectedIndex;
                        const selectedOption = event.target.options[selectedIndex];
                        const dataName = selectedOption.getAttribute('data-name');
                        const medicoId = event.target.value;
                        if (medicoId === "") {
                          setIsNotAvailableDays(false);
                          setSpecialism('Seleccionar médico para ver especialidad.')
                        } else {
                          isNotChangueDate = false;
                          fetchAvailableDates(medicoId);
                          getSpecialism(medicoId);
                        }
                        getMedicalPhoto(medicoId);
                        setFieldValue('medico', dataName);
                        setFieldValue('docId', medicoId);
                        setSelectedSpecialist(medicoId);
                        setTurnsByDate([]);
                        setFieldValue('dia', new Date());
                        setSelectedHour('');
                        setFieldValue('hora', '');
                      }}
                    >
                      <option value="" label="Seleccione un médico" />
                      {specialists.length > 0 && (
                        specialists.map((specialist) => (
                          <option key={specialist.cod} value={specialist.cod} data-name={`${specialist.names} ${specialist.firstLastname}`}>
                            Dr. {specialist.names} {specialist.firstLastname}
                          </option>
                        ))
                      )}
                    </Field>
                  </div>
                  <ErrorMessage className='error-message' name="docId" component="div" />
                </div>
                <div className='date-photo-item' style={{ justifyContent: specialism ? 'start' : 'center' }}>
                  <img src={medicalPhoto} alt="" className={medicalPhoto === "images/md-del-usuario.png" ? 'defaultPhoto' : ''} />
                  <div className='date-specialism-item'>
                    {specialism &&
                      <>
                        <h3>Sub especialidad:</h3>
                        <p>{specialism}</p>
                      </>
                    }
                  </div>
                </div>
              </div>
              <div className='date-container'>
                <div className='date-data-item'>
                  <DatePicker
                    selected={selectedDate}
                    inline
                    locale="es"
                    onChange={(date: Date | null) => {
                      setFieldValue('dia', date);
                      setFieldValue('hora', '');
                      setSelectedHour('');
                      if (date !== null) {
                        setSelectedDate(date);
                        fetchTurnsByDate(values.docId, date);
                      }
                    }}
                    includeDates={availableDays}
                    minDate={new Date()}
                    maxDate={addMonths(new Date(), 3)}
                    dateFormat="dd/MM/yyyy"
                  />
                  <ErrorMessage className='error-message' name="dia" component="div" />
                </div>
                <div className='date-data-item'>
                  <h3>Horas disponibles</h3>
                  {values.docId === "" &&
                    <div className='infoMessage'>
                      <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                      <p>  Seleccione médico y fecha para ver las horas disponibles.</p>
                    </div>}
                  {isLoading ? <Spinner /> : (
                    !isNotAvailableDays ? (<ul style={{ listStyle: 'none', padding: 0 }}>
                      {turnsByDate.length > 0 &&
                        turnsByDate.map((hora: AvailableHour) => (
                          <li key={hora.codHour} style={{ display: 'inline', marginRight: '10px' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setFieldValue('hourId', hora.codHour);
                                setFieldValue('hora', hora.availableHour)
                                setSelectedHour(hora.availableHour);
                              }}
                              style={{
                                backgroundColor: selectedHour === hora.availableHour ? 'lightblue' : 'white',
                              }}
                            >
                              {hora.availableHour}
                            </button>
                          </li>
                        ))}
                    </ul>) :
                      <div className='alertMessage'>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        <p>  Este especialista no tiene horas disponibles, por favor seleccione otro.</p>
                      </div>
                  )}
                  <ErrorMessage className='error-message' name="hora" component="div" />
                </div>
              </div>
              <div className='date-data-buttons'>
                <button className='date-data-button' type='button' onClick={() => navigate('/')} >Volver</button>
                <button className='date-data-button' type="submit">Continuar</button>
              </div>
            </Form>
          )}
        </Formik>
        <NotAvailableHour
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          onConfirm={() => reloadTurnsByDate()}
          messageCode={messageCode} />
      </div>
    </>
  );
};

export default MedicalDateForm;
